import { useState } from 'react'
import { Form, Head } from '@inertiajs/react'
import InputError from '@/components/input-error'
import TextLink from '@/components/text-link'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import AuthLayout from '@/layouts/auth-layout'
import { newIdentityPasswordResetPath, signInPath, signUpPath, requestOtpPath } from '@/routes'

type AuthMethod = 'password' | 'otp'

export default function Login() {
  const [authMethod, setAuthMethod] = useState<AuthMethod>('password')

  return (
    <AuthLayout
      title="Log in to your account"
      description={authMethod === 'password' 
        ? 'Enter your email and password below to log in' 
        : 'Enter your email to receive a login code'}
    >
      <Head title="Log in" />
      
      <div className="p-6">
        <div className="mb-6 flex rounded-lg bg-muted p-1">
        <button
          type="button"
          onClick={() => setAuthMethod('password')}
          className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
            authMethod === 'password'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Password
        </button>
        <button
          type="button"
          onClick={() => setAuthMethod('otp')}
          className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
            authMethod === 'otp'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Email Code
        </button>
      </div>

      {authMethod === 'password' ? (
        <PasswordLoginForm />
      ) : (
        <OtpRequestForm />
      )}

      <div className="text-muted-foreground mt-6 text-center text-sm">
        Don&apos;t have an account?{' '}
        <TextLink href={signUpPath()} tabIndex={5}>
          Sign up
        </TextLink>
      </div>
      </div>
    </AuthLayout>
  )
}

function PasswordLoginForm() {
  return (
    <Form
      method="post"
      action={signInPath()}
      resetOnSuccess={['password']}
      className="flex flex-col gap-6"
    >
      {({ processing, errors }) => (
        <div className="grid gap-6">
          <div className="grid gap-2">
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              autoFocus
              tabIndex={1}
              autoComplete="email"
              placeholder="email@example.com"
            />
            <InputError messages={errors.email} />
          </div>

          <div className="grid gap-2">
            <div className="flex items-center">
              <Label htmlFor="password">Password</Label>
              <TextLink
                href={newIdentityPasswordResetPath()}
                className="ml-auto text-sm"
                tabIndex={5}
              >
                Forgot password?
              </TextLink>
            </div>
            <Input
              id="password"
              type="password"
              name="password"
              required
              tabIndex={2}
              autoComplete="current-password"
              placeholder="Password"
            />
            <InputError messages={errors.password} />
          </div>

          <Button
            type="submit"
            className="mt-4 w-full"
            tabIndex={4}
            disabled={processing}
          >
            {processing && <Spinner />}
            Log in
          </Button>
        </div>
      )}
    </Form>
  )
}

function OtpRequestForm() {
  return (
    <Form
      method="post"
      action={requestOtpPath()}
      className="flex flex-col gap-6"
    >
      {({ processing, errors }) => (
        <div className="grid gap-6">
          <div className="grid gap-2">
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              autoFocus
              tabIndex={1}
              autoComplete="email"
              placeholder="email@example.com"
            />
            <InputError messages={errors.email} />
          </div>

          <Button
            type="submit"
            className="mt-4 w-full"
            tabIndex={2}
            disabled={processing}
          >
            {processing && <Spinner />}
            Send Login Code
          </Button>
        </div>
      )}
    </Form>
  )
}
